import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import Icon from "@/components/Icon";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;

function formatApiErrorDetail(detail) {
    if (detail == null) return "Something went wrong. Please try again.";
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail))
        return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
    if (detail && typeof detail.msg === "string") return detail.msg;
    return String(detail);
}

const inputCls =
    "w-full bg-elevated border border-ink/10 rounded-lg px-4 py-3 text-ink placeholder:text-ice/30 focus:border-cyan2 focus:ring-1 focus:ring-cyan2 outline-none transition-all duration-300";

export default function Admin() {
    const [user, setUser] = useState(undefined);
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [loginError, setLoginError] = useState("");
    const [loggingIn, setLoggingIn] = useState(false);
    const [enquiries, setEnquiries] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    const load = useCallback(async () => {
        setLoading(true);
        setError("");
        try {
            const res = await axios.get(`${API}/enquiries`, { withCredentials: true });
            setEnquiries(res.data);
        } catch (e) {
            if (e.response?.status === 401) setUser(null);
            else setError("Could not load enquiries.");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        axios
            .get(`${API}/auth/me`, { withCredentials: true })
            .then((res) => setUser(res.data))
            .catch(() => setUser(null));
    }, []);

    useEffect(() => {
        if (user) load();
    }, [user, load]);

    const submitLogin = async (ev) => {
        ev.preventDefault();
        setLoggingIn(true);
        setLoginError("");
        try {
            const { data } = await axios.post(
                `${API}/auth/login`,
                { email, password },
                { withCredentials: true }
            );
            setUser(data);
        } catch (e) {
            setLoginError(formatApiErrorDetail(e.response?.data?.detail));
        } finally {
            setLoggingIn(false);
        }
    };

    const logout = async () => {
        try {
            await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
        } catch { /* session already gone */ }
        setUser(null);
        setEnquiries([]);
    };

    if (user === undefined) {
        return (
            <div className="min-h-screen bg-navy flex items-center justify-center">
                <p className="font-mono2 text-sm tracking-[0.25em] uppercase text-cyan2 animate-energy" data-testid="admin-loading">
                    Checking session...
                </p>
            </div>
        );
    }

    if (!user) {
        return (
            <div className="min-h-screen bg-navy circuit-bg flex items-center justify-center px-6">
                <form
                    data-testid="admin-login-form"
                    onSubmit={submitLogin}
                    className="w-full max-w-sm rounded-2xl border border-ink/10 bg-surface p-8"
                >
                    <div className="flex items-center gap-2.5">
                        <img
                            src="/images/logo.png"
                            alt="Farakka Broadband logo"
                            className="w-9 h-9 rounded-full object-cover ring-1 ring-solar/50"
                        />
                        <span className="font-heading font-bold text-lg text-ink">
                            Farakka <span className="text-cyan2">Broadband</span>
                        </span>
                    </div>
                    <h1 className="font-heading text-2xl font-semibold text-ink mt-6">Admin Login</h1>
                    <p className="mt-1.5 text-sm text-ice/55">Sign in to view customer enquiries.</p>

                    {loginError && (
                        <p data-testid="admin-login-error" className="mt-4 rounded-lg border border-red-500/30 bg-red-500/10 px-3.5 py-2.5 text-sm text-red-400">
                            {loginError}
                        </p>
                    )}

                    <div className="mt-6 space-y-4">
                        <div>
                            <label htmlFor="admin-email" className="block text-sm font-medium text-ice/80 mb-2">Email</label>
                            <input
                                id="admin-email"
                                data-testid="admin-email-input"
                                type="email"
                                required
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder="admin email"
                                className={inputCls}
                                autoComplete="username"
                            />
                        </div>
                        <div>
                            <label htmlFor="admin-password" className="block text-sm font-medium text-ice/80 mb-2">Password</label>
                            <input
                                id="admin-password"
                                data-testid="admin-password-input"
                                type="password"
                                required
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                placeholder="password"
                                className={inputCls}
                                autoComplete="current-password"
                            />
                        </div>
                        <button
                            data-testid="admin-login-button"
                            type="submit"
                            disabled={loggingIn}
                            className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-gradient-to-r from-electric to-cyan2 px-8 py-3.5 font-bold text-ink hover:shadow-[0_0_40px_rgba(0,207,255,0.4)] disabled:opacity-60 transition-all duration-300"
                        >
                            {loggingIn ? "Signing in..." : "Sign In"}
                        </button>
                        <Link to="/" data-testid="admin-login-back" className="block text-center text-sm text-ice/50 hover:text-cyan2 transition-colors">
                            Back to site
                        </Link>
                    </div>
                </form>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-navy px-6 md:px-12 py-12">
            <div className="max-w-5xl mx-auto">
                <div className="flex items-center justify-between flex-wrap gap-4">
                    <div>
                        <p className="font-mono2 text-xs tracking-[0.25em] uppercase text-cyan2">Farakka Broadband</p>
                        <h1 className="font-heading text-3xl md:text-4xl font-bold text-ink mt-2" data-testid="admin-title">
                            Enquiries
                        </h1>
                    </div>
                    <div className="flex items-center gap-3">
                        <button
                            data-testid="admin-refresh-button"
                            onClick={load}
                            className="inline-flex items-center gap-2 rounded-full border border-ink/20 px-5 py-2.5 text-sm font-semibold text-ink hover:border-cyan2 hover:text-cyan2 transition-colors"
                        >
                            <Icon name="refresh" size={15} />
                            Refresh
                        </button>
                        <button
                            data-testid="admin-logout-button"
                            onClick={logout}
                            className="rounded-full border border-ink/20 px-5 py-2.5 text-sm font-semibold text-ink hover:border-red-400 hover:text-red-400 transition-colors"
                        >
                            Logout
                        </button>
                        <Link
                            data-testid="admin-back-home"
                            to="/"
                            className="rounded-full bg-gradient-to-r from-electric to-cyan2 px-5 py-2.5 text-sm font-bold text-ink"
                        >
                            Back to Site
                        </Link>
                    </div>
                </div>

                <p className="mt-3 text-sm text-ice/50" data-testid="admin-count">
                    Signed in as {user.email} · {loading ? "Loading..." : `${enquiries.length} enquir${enquiries.length === 1 ? "y" : "ies"} received`}
                </p>

                {error && (
                    <p data-testid="admin-error" className="mt-6 rounded-xl border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-400">
                        {error}
                    </p>
                )}

                {!loading && !error && enquiries.length === 0 && (
                    <p data-testid="admin-empty" className="mt-10 text-ice/50 text-sm">
                        No enquiries yet. New submissions from the contact form will appear here.
                    </p>
                )}

                <div className="mt-8 space-y-4">
                    {enquiries.map((q) => (
                        <article
                            key={q.id}
                            data-testid={`admin-enquiry-${q.id}`}
                            className="rounded-2xl border border-ink/8 bg-surface p-6"
                        >
                            <div className="flex flex-wrap items-center justify-between gap-3">
                                <div>
                                    <h2 className="font-heading text-lg font-semibold text-ink">{q.name}</h2>
                                    <a href={`tel:${q.phone}`} className="text-sm text-cyan2 hover:underline">
                                        {q.phone}
                                    </a>
                                </div>
                                <div className="text-right">
                                    <span className="inline-block rounded-full border border-electric/40 bg-electric/10 px-3 py-1 text-xs font-semibold text-ice/85">
                                        {q.service}
                                    </span>
                                    <p className="mt-1.5 text-xs text-ice/40">
                                        {new Date(q.created_at).toLocaleString("en-IN")}
                                    </p>
                                </div>
                            </div>
                            {q.message && (
                                <p className="mt-3 text-sm text-ice/65 leading-relaxed border-t border-ink/5 pt-3">
                                    {q.message}
                                </p>
                            )}
                        </article>
                    ))}
                </div>
            </div>
        </div>
    );
}
